/**
 * Customers — Phase 4.
 *
 * Lists every row in `customers`, expandable to a per-customer detail panel
 * showing the linked subscriptions, the linked device(s), and three primary
 * mutating actions:
 *
 *   • Create customer (top-right primary button)
 *   • Create subscription on an existing customer — REQUIRED tier dropdown
 *     (weekly | monthly), surfaces per-tier billing cadence in helper text.
 *   • Cancel an active subscription (DELETE)
 *   • Link a device to a customer (admin-side claim)
 *
 * Tier UX:
 *   - "Switch tier" is INTENTIONALLY not exposed — Stripe doesn't allow
 *     in-place product swaps when the tiers are separate Products. Operators
 *     cancel the existing subscription and create a new one on the desired
 *     tier (the prompt locks this design).
 *   - Tier badge: teal for weekly, orange for monthly. Weekly = brand-teal
 *     to read as "shorter cycle"; monthly = brand-orange to read as "main
 *     cadence". Distinct enough to scan at a glance in the customer list.
 *
 * Style: same surface tokens + chip patterns as Devices/Releases.
 *
 * react-query: every mutation invalidates `["customers"]` plus the relevant
 * `["customer", id]` or `["devices"]` cache so the UI snaps to truth without
 * the operator hitting refresh.
 */

import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import PageHeader from "@/components/PageHeader";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "@/components/StatusStates";
import ZelloPanel from "@/components/ZelloPanel";
import {
  ApiError,
  cancelSubscription,
  createComplimentarySubscription,
  createCustomer,
  createSubscription,
  getCustomer,
  linkDevice,
  listCustomers,
  listDevices,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type {
  ComplimentarySubscriptionCreateInputs,
  Customer,
  Device,
  Subscription,
  SubscriptionCreateInputs,
} from "@/lib/types";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Customers() {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const customersQ = useQuery({
    queryKey: ["customers"],
    queryFn: listCustomers,
  });

  const filtered = useMemo(() => {
    const all = customersQ.data ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.stripe_customer_id ?? "").toLowerCase().includes(q),
    );
  }, [customersQ.data, filter]);

  const createMutation = useMutation({
    mutationFn: createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle={
          customersQ.data
            ? `${customersQ.data.length} customer${customersQ.data.length === 1 ? "" : "s"} on file`
            : "Loading customers…"
        }
      />

      <div className="px-8 py-6 space-y-6">
        <CreateCustomerCard mutation={createMutation} />

        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wider text-brand-dim">
              Search
            </label>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Name / email / Stripe id"
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange w-72"
            />
          </div>
        </div>

        <div className="rounded-md border border-surface-border bg-surface overflow-hidden">
          {customersQ.isLoading ? (
            <LoadingBlock label="Loading customers…" />
          ) : customersQ.isError ? (
            <ErrorBlock
              error={customersQ.error}
              onRetry={() => customersQ.refetch()}
            />
          ) : !filtered || filtered.length === 0 ? (
            <EmptyBlock
              title={filter ? "No matches." : "No customers yet."}
              description={
                filter
                  ? "Try a different search."
                  : "Create one with the form above."
              }
            />
          ) : (
            <CustomersTable
              customers={filtered}
              expandedId={expandedId}
              onToggle={(id) =>
                setExpandedId((prev) => (prev === id ? null : id))
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Create-customer card
// ---------------------------------------------------------------------------

function CreateCustomerCard({
  mutation,
}: {
  mutation: UseMutationResult<
    Customer,
    unknown,
    { name: string; email: string }
  >;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const canSubmit = name.trim().length > 0 && /\S+@\S+\.\S+/.test(email);

  return (
    <Card title="Create customer">
      <form
        className="grid gap-3 sm:grid-cols-3 sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit || mutation.isPending) return;
          mutation.mutate(
            { name: name.trim(), email: email.trim() },
            {
              onSuccess: () => {
                setName("");
                setEmail("");
              },
            },
          );
        }}
      >
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-brand-dim">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Racing"
            className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            autoComplete="off"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-brand-dim">
            Email
          </label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ops@acme.com"
            type="email"
            className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            autoComplete="off"
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={!canSubmit || mutation.isPending}
            className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? "Creating…" : "Create customer"}
          </button>
        </div>
      </form>
      <p className="mt-2 text-[11px] text-brand-dim">
        Creates a Stripe customer and persists the local row. Subscriptions
        are added separately, on the customer's expanded row below.
      </p>
      {mutation.isError ? (
        <div className="mt-3">
          <ErrorBlock error={mutation.error} />
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Customers table
// ---------------------------------------------------------------------------

function CustomersTable({
  customers,
  expandedId,
  onToggle,
}: {
  customers: Customer[];
  expandedId: string | null;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
          <tr>
            <th className="px-4 py-2 font-medium w-8" />
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-4 py-2 font-medium">Email</th>
            <th className="px-4 py-2 font-medium">Stripe id</th>
            <th className="px-4 py-2 font-medium">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {customers.map((c) => {
            const isOpen = expandedId === c.id;
            return (
              <>
                <tr
                  key={c.id}
                  onClick={() => onToggle(c.id)}
                  className="hover:bg-surface-raised/60 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2 text-brand-dim">
                    <span className="inline-block w-3 select-none">
                      {isOpen ? "▾" : "▸"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-100 font-medium">
                    {c.name}
                  </td>
                  <td className="px-4 py-2 text-slate-200">{c.email}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-300">
                    {c.stripe_customer_id ?? (
                      <span className="text-brand-dim">—</span>
                    )}
                  </td>
                  <td
                    className="px-4 py-2 text-xs text-slate-300"
                    title={c.created_at}
                  >
                    {formatDateTime(c.created_at)}
                  </td>
                </tr>
                {isOpen ? (
                  <tr key={`${c.id}-detail`} className="bg-surface-sunken/40">
                    <td colSpan={5} className="px-0 py-0">
                      <CustomerDetailPanel customer={c} />
                    </td>
                  </tr>
                ) : null}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer detail panel — fetched lazily on expand
// ---------------------------------------------------------------------------

function CustomerDetailPanel({ customer }: { customer: Customer }) {
  const queryClient = useQueryClient();
  const detailQ = useQuery({
    queryKey: ["customer", customer.id],
    queryFn: () => getCustomer(customer.id),
  });
  const devicesQ = useQuery({
    queryKey: ["devices", { customer: customer.id }],
    queryFn: () => listDevices({ customer: customer.id }),
  });
  const allDevicesQ = useQuery({
    queryKey: ["devices"],
    queryFn: () => listDevices({}),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["customer", customer.id] });
    queryClient.invalidateQueries({ queryKey: ["customers"] });
    queryClient.invalidateQueries({ queryKey: ["devices"] });
  };

  const createSubMutation = useMutation({
    mutationFn: (inputs: SubscriptionCreateInputs) =>
      createSubscription(customer.id, inputs),
    onSuccess: invalidate,
  });

  // Comp grants go to a different endpoint that skips Stripe — see
  // backend handlers/admin_customers.ts:handleCreateComplimentarySubscription.
  const createCompSubMutation = useMutation({
    mutationFn: (inputs: ComplimentarySubscriptionCreateInputs) =>
      createComplimentarySubscription(customer.id, inputs),
    onSuccess: invalidate,
  });

  const linkDeviceMutation = useMutation({
    mutationFn: (serial: string) => linkDevice(customer.id, serial),
    onSuccess: invalidate,
  });

  const cancelSubMutation = useMutation({
    mutationFn: (subscriptionId: string) =>
      cancelSubscription(customer.id, subscriptionId),
    onSuccess: invalidate,
  });

  return (
    <div className="px-6 py-5 space-y-5 border-t border-surface-border">
      {detailQ.isLoading ? (
        <LoadingBlock label="Loading customer detail…" />
      ) : detailQ.isError ? (
        <ErrorBlock error={detailQ.error} onRetry={() => detailQ.refetch()} />
      ) : !detailQ.data ? null : (
        <>
          <SubscriptionPanel
            customerId={customer.id}
            subscriptions={detailQ.data.subscriptions}
            customerDevices={devicesQ.data ?? []}
            allDevices={allDevicesQ.data ?? []}
            createMutation={createSubMutation}
            createCompMutation={createCompSubMutation}
            cancelMutation={cancelSubMutation}
          />

          <DevicesPanel
            customerName={customer.name}
            customerDevices={devicesQ.data ?? []}
            allDevices={allDevicesQ.data ?? []}
            linkMutation={linkDeviceMutation}
          />

          <ZelloPanel
            customer={customer}
            customerDevices={devicesQ.data ?? []}
            allDevices={allDevicesQ.data ?? []}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscription panel
// ---------------------------------------------------------------------------

function SubscriptionPanel({
  customerId: _customerId,
  subscriptions,
  customerDevices,
  allDevices,
  createMutation,
  createCompMutation,
  cancelMutation,
}: {
  customerId: string;
  subscriptions: Subscription[];
  /** Devices already linked to THIS customer — preferred targets for comp. */
  customerDevices: Device[];
  /** Full fleet — used to surface unlinked devices for comp grants. */
  allDevices: Device[];
  createMutation: UseMutationResult<
    Subscription,
    unknown,
    SubscriptionCreateInputs
  >;
  createCompMutation: UseMutationResult<
    Subscription,
    unknown,
    ComplimentarySubscriptionCreateInputs
  >;
  cancelMutation: UseMutationResult<
    { stripe_subscription_id: string; status: string },
    unknown,
    string
  >;
}) {
  // Phase A — operators can mint Messaging (Twilio), Voice (Zello), or
  // Bundle (both). We model "plan" + "tier" as orthogonal dropdowns:
  //   - plan picks WHAT service the customer is paying for
  //   - tier picks billing cadence (weekly/monthly) OR comp routing
  // Voice has no weekly cadence (Zello has a 30-day minimum) so we
  // hide weekly when plan=voice. Comp routing is unchanged: when
  // tier=complimentary we POST to /complimentary-subscription
  // regardless of plan; the backend builds the appropriate payload.
  const [plan, setPlan] = useState<"messaging" | "voice" | "bundle">(
    "messaging",
  );
  const [tier, setTier] = useState<"weekly" | "monthly" | "complimentary">(
    "monthly",
  );
  const [users, setUsers] = useState<number>(2);
  const [channels, setChannels] = useState<number>(1);
  const [compDeviceSerial, setCompDeviceSerial] = useState("");
  const [compExpiresAt, setCompExpiresAt] = useState("");
  const [compNotes, setCompNotes] = useState("");

  // Devices the operator can grant comp on: this customer's already-linked
  // devices, plus any unlinked devices in the fleet (the backend will
  // auto-link them on grant). Excludes devices linked to OTHER customers.
  const grantableDevices = useMemo(() => {
    const seen = new Set(customerDevices.map((d) => d.serial));
    const merged: Device[] = [...customerDevices];
    for (const d of allDevices) {
      if (seen.has(d.serial)) continue;
      if (d.customer_id) continue; // owned elsewhere — skip
      merged.push(d);
    }
    return merged;
  }, [customerDevices, allDevices]);

  const isComp = tier === "complimentary";
  const isVoiceOrBundle = plan === "voice" || plan === "bundle";
  const compReady = isComp && compDeviceSerial.length > 0;
  const isCreating =
    createMutation.isPending || createCompMutation.isPending;

  return (
    <Card
      title="Subscriptions"
      subtitle={`${subscriptions.length} on record`}
    >
      <div className="space-y-4">
        <form
          className="grid gap-3 sm:grid-cols-[180px_180px_auto_1fr] sm:items-end rounded border border-surface-border bg-surface-sunken/60 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (isCreating) return;
            if (isComp) {
              if (!compReady) return;
              const inputs: ComplimentarySubscriptionCreateInputs = {
                device_serial: compDeviceSerial,
                plan,
                expires_at: compExpiresAt
                  ? new Date(compExpiresAt).toISOString()
                  : null,
                notes: compNotes.trim() || undefined,
              };
              if (isVoiceOrBundle) {
                inputs.users = users;
                inputs.channels = channels;
              }
              createCompMutation.mutate(inputs, {
                onSuccess: () => {
                  setCompDeviceSerial("");
                  setCompExpiresAt("");
                  setCompNotes("");
                },
              });
            } else {
              const inputs: SubscriptionCreateInputs = { plan };
              // Voice has no tier (Zello is monthly-only); messaging
              // and bundle both honor the chosen weekly/monthly value.
              if (plan !== "voice") {
                inputs.tier = tier as "weekly" | "monthly";
              }
              if (isVoiceOrBundle) {
                inputs.users = users;
                inputs.channels = channels;
              }
              createMutation.mutate(inputs);
            }
          }}
        >
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wider text-brand-dim">
              Plan (required)
            </label>
            <select
              value={plan}
              onChange={(e) => {
                const next = e.target.value as
                  | "messaging"
                  | "voice"
                  | "bundle";
                setPlan(next);
                // Voice has no weekly cadence — auto-flip if needed.
                if (next === "voice" && tier === "weekly") setTier("monthly");
              }}
              className="bg-surface border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            >
              <option value="messaging">Messaging (Twilio)</option>
              <option value="voice">Voice (Zello)</option>
              <option value="bundle">Bundle (both)</option>
            </select>
            <span className="text-[11px] text-brand-dim leading-snug">
              {plan === "messaging"
                ? "Twilio SMS only. Tier picks weekly/monthly billing cadence."
                : plan === "voice"
                  ? "Zello voice channels. Monthly only — Zello's 30-day minimum."
                  : "Twilio SMS + Zello voice on one Stripe sub."}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-wider text-brand-dim">
              Tier (required)
            </label>
            <select
              value={tier}
              onChange={(e) =>
                setTier(
                  e.target.value as "weekly" | "monthly" | "complimentary",
                )
              }
              className="bg-surface border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            >
              <option value="monthly">Monthly</option>
              {plan !== "voice" ? (
                <option value="weekly">Weekly</option>
              ) : null}
              <option value="complimentary">Complimentary (free)</option>
            </select>
            <span className="text-[11px] text-brand-dim leading-snug">
              {tier === "weekly"
                ? "Weekly billing — recurs every 7 days. Same fleet features as Monthly."
                : tier === "monthly"
                  ? "Monthly billing — recurs every calendar month."
                  : "Operator-granted free service. No Stripe charge. Optional expiration."}
            </span>
          </div>
          <button
            type="submit"
            disabled={isCreating || (isComp && !compReady)}
            className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50 self-start"
          >
            {isCreating
              ? isComp
                ? "Granting…"
                : "Creating…"
              : isComp
                ? "Grant complimentary"
                : "Create subscription"}
          </button>
          <p className="text-[11px] text-brand-dim leading-snug self-center">
            {isComp
              ? "Comp grants flip the chosen device's subscription_active gate immediately. Revoke from the row below to schedule a SIM pause."
              : "Switching plan/tier in-place is not supported. To change, cancel this subscription and create a new one."}
          </p>

          {isVoiceOrBundle ? (
            <>
              <div className="flex flex-col gap-1 sm:col-start-1">
                <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                  Total Zello users
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={users}
                  onChange={(e) =>
                    setUsers(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  className="bg-surface border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
                />
                <span className="text-[11px] text-brand-dim leading-snug">
                  Includes the device user. Base bundle covers 2 (device + 1 operator).
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                  Total Zello channels
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={channels}
                  onChange={(e) =>
                    setChannels(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  className="bg-surface border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
                />
                <span className="text-[11px] text-brand-dim leading-snug">
                  Base bundle covers 1 channel. Each extra is a per-channel add-on.
                </span>
              </div>
            </>
          ) : null}

          {isComp ? (
            <>
              <div className="flex flex-col gap-1 sm:col-start-1">
                <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                  Device (required)
                </label>
                <select
                  value={compDeviceSerial}
                  onChange={(e) => setCompDeviceSerial(e.target.value)}
                  className="bg-surface border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
                >
                  <option value="">Pick a device…</option>
                  {grantableDevices.map((d) => {
                    const linked =
                      d.customer_id && customerDevices.some((c) => c.serial === d.serial);
                    return (
                      <option key={d.serial} value={d.serial}>
                        {(d.device_label ?? d.serial) +
                          (d.device_label ? ` · ${d.serial}` : "") +
                          (linked ? " (already linked)" : " (unlinked)")}
                      </option>
                    );
                  })}
                </select>
                <span className="text-[11px] text-brand-dim leading-snug">
                  Unlinked devices will be auto-linked to this customer.
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                  Expires (optional)
                </label>
                <input
                  type="datetime-local"
                  value={compExpiresAt}
                  onChange={(e) => setCompExpiresAt(e.target.value)}
                  className="bg-surface border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
                />
                <span className="text-[11px] text-brand-dim leading-snug">
                  Leave blank for perpetual. Operator must revoke manually.
                </span>
              </div>
              <div className="flex flex-col gap-1 sm:col-span-4">
                <label className="text-[11px] uppercase tracking-wider text-brand-dim">
                  Notes (optional, audit-only)
                </label>
                <input
                  type="text"
                  value={compNotes}
                  onChange={(e) => setCompNotes(e.target.value)}
                  placeholder="e.g. demo unit, sponsorship through 2026 season"
                  className="bg-surface border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
                />
              </div>
            </>
          ) : null}
        </form>

        {createMutation.isError ? (
          <ErrorBlock error={createMutation.error} />
        ) : null}
        {createCompMutation.isError ? (
          <ErrorBlock error={createCompMutation.error} />
        ) : null}

        {subscriptions.length === 0 ? (
          <EmptyBlock
            title="No subscriptions yet."
            description="The customer has no Stripe subscription on file."
          />
        ) : (
          <div className="overflow-x-auto -mx-4 -my-2">
            <table className="w-full text-sm">
              <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
                <tr>
                  <th className="px-4 py-2 font-medium">Plan</th>
                  <th className="px-4 py-2 font-medium">Tier</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Stripe id</th>
                  <th className="px-4 py-2 font-medium">Period end</th>
                  <th className="px-4 py-2 font-medium">Device</th>
                  <th className="px-4 py-2 font-medium text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-surface-raised/40">
                    <td className="px-4 py-2">
                      <PlanBadge plan={sub.plan} complimentary={sub.complimentary} />
                    </td>
                    <td className="px-4 py-2">
                      <TierBadge tier={sub.tier} />
                    </td>
                    <td className="px-4 py-2">
                      <SubStatusBadge status={sub.status} />
                      {sub.cancel_at_period_end ? (
                        <span className="ml-2 text-[11px] text-amber-300">
                          cancels at period end
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-300 truncate max-w-[180px]">
                      {sub.complimentary
                        ? sub.granted_by
                          ? `comp · granted by ${sub.granted_by}`
                          : "comp"
                        : sub.stripe_subscription_id}
                    </td>
                    <td
                      className="px-4 py-2 text-xs text-slate-300"
                      title={sub.current_period_end ?? ""}
                    >
                      {sub.current_period_end
                        ? formatDateTime(sub.current_period_end)
                        : "—"}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-300">
                      {sub.device_serial ?? (
                        <span className="text-brand-dim">unlinked</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {isCancellable(sub.status) ? (
                        <button
                          type="button"
                          onClick={() => {
                            const msg = sub.complimentary
                              ? "Revoke this complimentary subscription? The Hologram SIM will pause after the grace period."
                              : `Cancel subscription ${sub.stripe_subscription_id}? The Hologram SIM will pause after the grace period.`;
                            if (!window.confirm(msg)) return;
                            cancelMutation.mutate(sub.id);
                          }}
                          disabled={cancelMutation.isPending}
                          className="inline-flex items-center rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-xs uppercase tracking-wider text-rose-200 hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      ) : (
                        <span className="text-[11px] text-brand-dim">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cancelMutation.isError ? (
          <ErrorBlock error={cancelMutation.error} />
        ) : null}
      </div>
    </Card>
  );
}

function isCancellable(status: string): boolean {
  return ["active", "trialing", "past_due", "unpaid"].includes(status);
}

function TierBadge({ tier }: { tier: string | null }) {
  if (!tier) {
    return (
      <span
        title="Stripe price_id didn't match either configured tier — check STRIPE_PRICE_ID_*"
        className="inline-flex items-center rounded-full bg-surface-sunken text-brand-dim ring-1 ring-inset ring-surface-border px-2 py-0.5 text-xs uppercase tracking-wider"
      >
        unknown
      </span>
    );
  }
  if (tier === "weekly") {
    return (
      <span className="inline-flex items-center rounded-full bg-brand-teal/15 text-cyan-200 ring-1 ring-inset ring-brand-teal/40 px-2 py-0.5 text-xs uppercase tracking-wider">
        Weekly
      </span>
    );
  }
  if (tier === "monthly") {
    return (
      <span className="inline-flex items-center rounded-full bg-brand-orange/15 text-orange-300 ring-1 ring-inset ring-brand-orange/40 px-2 py-0.5 text-xs uppercase tracking-wider">
        Monthly
      </span>
    );
  }
  if (tier === "complimentary") {
    // Violet reads "different from billed" at a glance — distinct from
    // both the teal (weekly) and orange (monthly) paid badges so the
    // operator can scan a customer list and immediately spot comp rows.
    return (
      <span
        title="Operator-granted free service — no Stripe billing."
        className="inline-flex items-center rounded-full bg-violet-500/15 text-violet-200 ring-1 ring-inset ring-violet-500/40 px-2 py-0.5 text-xs uppercase tracking-wider"
      >
        Comp
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border px-2 py-0.5 text-xs uppercase tracking-wider">
      {tier}
    </span>
  );
}

/**
 * Phase A — visual badge for the service plan a subscription pays for.
 * The colour palette is intentionally distinct from TierBadge so an
 * operator scanning the row can read PLAN (what service) and TIER
 * (billing cadence) as orthogonal axes:
 *
 *   Messaging  — sky blue   (Twilio / SMS)
 *   Voice      — emerald    (Zello / live audio)
 *   Bundle     — amber      (both — "warm" colour reads "premium")
 *
 * The `complimentary` prop overrides the colour with a neutral slate
 * so comp rows don't visually compete with paid plans for attention.
 */
function PlanBadge({
  plan,
  complimentary,
}: {
  plan: string | null;
  complimentary: boolean;
}) {
  if (!plan) {
    return (
      <span
        title="Webhook didn't recognize the price_id — check STRIPE_PRICE_ID_* env vars"
        className="inline-flex items-center rounded-full bg-surface-sunken text-brand-dim ring-1 ring-inset ring-surface-border px-2 py-0.5 text-xs uppercase tracking-wider"
      >
        unknown
      </span>
    );
  }
  const compClass =
    "inline-flex items-center rounded-full bg-slate-500/15 text-slate-200 ring-1 ring-inset ring-slate-500/40 px-2 py-0.5 text-xs uppercase tracking-wider";
  if (plan === "messaging") {
    return (
      <span
        title="Twilio SMS service"
        className={
          complimentary
            ? compClass
            : "inline-flex items-center rounded-full bg-sky-500/15 text-sky-200 ring-1 ring-inset ring-sky-500/40 px-2 py-0.5 text-xs uppercase tracking-wider"
        }
      >
        Messaging
      </span>
    );
  }
  if (plan === "voice") {
    return (
      <span
        title="Zello Work voice channels"
        className={
          complimentary
            ? compClass
            : "inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-200 ring-1 ring-inset ring-emerald-500/40 px-2 py-0.5 text-xs uppercase tracking-wider"
        }
      >
        Voice
      </span>
    );
  }
  if (plan === "bundle") {
    return (
      <span
        title="Twilio SMS + Zello voice"
        className={
          complimentary
            ? compClass
            : "inline-flex items-center rounded-full bg-amber-500/15 text-amber-200 ring-1 ring-inset ring-amber-500/40 px-2 py-0.5 text-xs uppercase tracking-wider"
        }
      >
        Bundle
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border px-2 py-0.5 text-xs uppercase tracking-wider">
      {plan}
    </span>
  );
}

function SubStatusBadge({ status }: { status: string }) {
  let cls =
    "bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border";
  switch (status) {
    case "active":
    case "trialing":
      cls =
        "bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30";
      break;
    case "past_due":
    case "unpaid":
      cls =
        "bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30";
      break;
    case "canceled":
    case "incomplete_expired":
      cls = "bg-rose-500/15 text-rose-300 ring-1 ring-inset ring-rose-500/30";
      break;
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Devices panel
// ---------------------------------------------------------------------------

function DevicesPanel({
  customerName,
  customerDevices,
  allDevices,
  linkMutation,
}: {
  customerName: string;
  customerDevices: Device[];
  allDevices: Device[];
  linkMutation: UseMutationResult<Device, unknown, string>;
}) {
  const unlinked = useMemo(
    () => allDevices.filter((d) => !d.customer_id),
    [allDevices],
  );
  const [pickedSerial, setPickedSerial] = useState("");

  return (
    <Card
      title="Linked devices"
      subtitle={`${customerDevices.length} device${customerDevices.length === 1 ? "" : "s"}`}
    >
      <div className="space-y-3">
        {customerDevices.length === 0 ? (
          <p className="text-sm text-brand-dim">
            No devices linked to <span className="text-slate-100">{customerName}</span> yet.
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {customerDevices.map((d) => (
              <li
                key={d.serial}
                className="flex items-center justify-between rounded border border-surface-border bg-surface-sunken/60 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 truncate">
                    {d.device_label ?? "—"}
                  </div>
                  <div className="font-mono text-[11px] text-slate-400 truncate">
                    {d.serial}
                  </div>
                </div>
                <span
                  className={`text-[11px] uppercase tracking-wider ${d.subscription_active ? "text-emerald-300" : "text-rose-300"}`}
                >
                  {d.subscription_active ? "active" : "inactive"}
                </span>
              </li>
            ))}
          </ul>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = pickedSerial.trim();
            if (!trimmed) return;
            linkMutation.mutate(trimmed, {
              onSuccess: () => setPickedSerial(""),
            });
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <div className="flex flex-col gap-1 grow min-w-[260px]">
            <label className="text-[11px] uppercase tracking-wider text-brand-dim">
              Link existing device (unassigned)
            </label>
            <select
              value={pickedSerial}
              onChange={(e) => setPickedSerial(e.target.value)}
              className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            >
              <option value="">Pick a device…</option>
              {unlinked.map((d) => (
                <option key={d.serial} value={d.serial}>
                  {d.device_label ?? d.serial}
                  {d.device_label ? ` · ${d.serial}` : ""}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={!pickedSerial || linkMutation.isPending}
            className="inline-flex items-center rounded border border-brand-teal/40 bg-brand-teal/15 px-2.5 py-1 text-xs uppercase tracking-wider text-cyan-200 hover:bg-brand-teal/25 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {linkMutation.isPending ? "Linking…" : "Link device"}
          </button>
        </form>
        {linkMutation.isError ? (
          <ErrorBlock error={linkMutation.error} />
        ) : null}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Card primitive (mirrors Devices/Releases styling)
// ---------------------------------------------------------------------------

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-surface-border bg-surface">
      <header className="flex items-center justify-between border-b border-surface-border px-5 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-100">
          {title}
        </h2>
        {subtitle ? (
          <span className="text-xs text-brand-dim">{subtitle}</span>
        ) : null}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

// `ApiError` is intentionally re-exported via lib/api so a future devtools
// extension could `instanceof`-test this without re-importing the source.
export { ApiError };
